/**
 * The dashboard's card for the latest AI analysis.
 *
 * Deliberately a summary, not the analysis: the strategy agent's full output —
 * priorities with their grounding, signal conflicts, the recommended order of
 * work — needs room to be read, and lives on the analysis page. What belongs
 * here is enough to answer "is there anything new, and is it worth reading?"
 *
 * It owns its whole card, header and run button included. Splitting the frame
 * across the dashboard and the body across this file left nothing in charge of
 * how the card looked as a unit, which is exactly how it ended up looking like
 * two things stacked.
 */

import { Link } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { ApiError } from '@/api/client';
import { aiApi } from '@/api/endpoints';
import { Button, Card, ConfidenceRing, Spinner } from '@/components/ui';
import { AlertIcon, ArrowRightIcon, SparkIcon } from '@/components/icons';
import { cn, confidenceTone, formatRelative } from '@/utils/format';
import type { AgentRunSummary, AiStatus } from '@/types/api';

const PREVIEW_COUNT = 2;

export function AiAnalysisPanel({
  siteId,
  status,
  hasAudit,
}: {
  siteId: string;
  status: AiStatus;
  hasAudit: boolean;
}) {
  const queryClient = useQueryClient();

  const runsQuery = useQuery({
    queryKey: ['ai-runs', siteId],
    queryFn: () => aiApi.list(siteId, { page_size: 1 }),
    enabled: status.enabled,
    refetchInterval: (query) => {
      const page = query.state.data as { items: AgentRunSummary[] } | undefined;
      const runStatus = page?.items?.[0]?.status;
      return runStatus === 'running' || runStatus === 'queued' ? 4000 : false;
    },
  });

  const latest = runsQuery.data?.items?.[0];

  const detailQuery = useQuery({
    queryKey: ['ai-run', latest?.id],
    queryFn: () => aiApi.get(latest!.id),
    enabled: Boolean(latest && latest.status === 'completed'),
  });

  const runMutation = useMutation({
    mutationFn: () => aiApi.start(siteId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['ai-runs', siteId] }),
  });

  const inFlight = latest?.status === 'queued' || latest?.status === 'running';

  return (
    <Card padded={false} className="overflow-hidden">
      {/* A tinted head marks this as the one card on the dashboard whose
          contents were written rather than measured. */}
      <div className="flex items-center gap-2.5 border-b border-border-subtle bg-brand-subtle/60 px-4 py-3">
        <span className="grid h-7 w-7 shrink-0 place-items-center rounded-lg bg-brand text-white">
          <SparkIcon className="h-4 w-4" />
        </span>
        <h2 className="min-w-0 flex-1 truncate text-sm font-semibold text-ink">AI analysis</h2>
        {status.enabled ? (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => runMutation.mutate()}
            loading={runMutation.isPending || inFlight}
            disabled={!hasAudit || inFlight}
            title={
              !hasAudit
                ? 'Run an audit first'
                : inFlight
                  ? 'An analysis is already running'
                  : undefined
            }
            ariaLabel="Run AI analysis"
          >
            {inFlight ? '' : latest ? 'Rerun' : 'Run'}
          </Button>
        ) : null}
      </div>

      <div className="px-4 py-4">
        <Body
          siteId={siteId}
          status={status}
          latest={latest}
          detail={detailQuery.data}
          error={runMutation.error}
        />
      </div>
    </Card>
  );
}

function Body({
  siteId,
  status,
  latest,
  detail,
  error,
}: {
  siteId: string;
  status: AiStatus;
  latest: AgentRunSummary | undefined;
  detail: import('@/types/api').AgentRunDetail | undefined;
  error: unknown;
}) {
  if (error) {
    return (
      <p className="flex items-start gap-2 text-xs text-danger">
        <AlertIcon className="mt-px h-4 w-4 shrink-0" />
        {error instanceof ApiError ? error.message : 'The analysis could not be started.'}
      </p>
    );
  }

  if (!status.enabled) {
    return (
      <p className="text-xs leading-relaxed text-ink-subtle">
        Not configured. Everything else — crawling, auditing, scoring, execution and verification —
        works without it.
      </p>
    );
  }

  if (!latest) {
    return (
      <p className="text-xs leading-relaxed text-ink-subtle">
        No analysis yet. Run one to interpret the latest audit: what to do first, and in what order.
      </p>
    );
  }

  if (latest.status === 'queued' || latest.status === 'running') {
    return (
      <p className="flex items-center gap-2 text-xs text-ink-muted">
        <Spinner className="h-3.5 w-3.5" />
        Analysis {latest.status}… started {formatRelative(latest.created_at)}.
      </p>
    );
  }

  if (latest.status === 'failed') {
    return (
      <p className="flex items-start gap-2 text-xs text-ink-muted">
        <AlertIcon className="mt-px h-4 w-4 shrink-0 text-danger" />
        The last analysis failed. Nothing was changed on your website — you can run it again.
      </p>
    );
  }

  const output = detail?.output_summary;
  if (!output) {
    return (
      <p className="flex items-center gap-2 text-xs text-ink-muted">
        <Spinner className="h-3.5 w-3.5" />
        Loading the analysis…
      </p>
    );
  }

  const priorities = output.strategic_priorities ?? [];
  const conflicts = (output.signal_conflicts ?? []).length;
  const confidence = output.overall_confidence ?? 0;

  return (
    <div className="space-y-3.5">
      <div className="flex items-center gap-3.5">
        <ConfidenceRing value={confidence} size="sm" showBand={false} />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-ink">
            {priorities.length} priorit{priorities.length === 1 ? 'y' : 'ies'}
          </p>
          <p className="mt-0.5 text-[11px] text-ink-subtle">
            {conflicts > 0 ? `${conflicts} conflicting signal${conflicts === 1 ? '' : 's'}` : 'no conflicts found'}
            {' · '}
            {confidence >= 0.7 ? 'well supported' : confidence >= 0.4 ? 'moderate' : 'low'} confidence
          </p>
        </div>
      </div>

      {output.strategy_summary ? (
        <p className="clamp-3 text-[13px] leading-relaxed text-ink-muted">
          {output.strategy_summary}
        </p>
      ) : null}

      {priorities.length > 0 ? (
        <ol className="space-y-2">
          {priorities.slice(0, PREVIEW_COUNT).map((priority, index) => {
            const rail = { success: 'bg-success', brand: 'bg-brand', warning: 'bg-warning' }[
              confidenceTone(priority.confidence)
            ];
            return (
              <li
                key={`${priority.title}-${index}`}
                className="relative overflow-hidden rounded-lg bg-surface-muted py-2 pr-2.5 pl-3.5"
              >
                {/* Same colour language as the analysis page, so a priority
                    keeps its identity between the two screens. */}
                <span aria-hidden="true" className={cn('absolute inset-y-0 left-0 w-0.5', rail)} />
                <p className="clamp-2 text-xs leading-relaxed font-medium text-ink">
                  {priority.title}
                </p>
              </li>
            );
          })}
        </ol>
      ) : null}

      <Link
        to={`/sites/${siteId}/analysis`}
        className="group flex items-center gap-1 text-xs font-medium text-brand hover:underline"
      >
        {priorities.length > PREVIEW_COUNT
          ? `See all ${priorities.length} priorities`
          : 'Read the full analysis'}
        <ArrowRightIcon className="h-3 w-3 transition-transform group-hover:translate-x-0.5" />
      </Link>

      <p className="border-t border-border-subtle pt-2.5 text-[11px] leading-relaxed text-ink-subtle">
        Interpretation only — no changes were made. {formatRelative(
          latest.finished_at ?? latest.created_at,
        )}
        {' · '}${latest.estimated_cost_usd.toFixed(4)}
      </p>
    </div>
  );
}
